import { cpFetch } from '../../lib/cpClient';
import type { ApiCatalog, TestRequest, TestResponse, ApiChangeSet, DryRunResponse } from './types';
import { ApiCatalogSchema, TestResponseSchema, DryRunResponseSchema } from './schemas';

// API client functions

export async function fetchApiCatalog(token: string, tenant: string): Promise<ApiCatalog> {
  const response = await cpFetch('/api-manager/catalog', {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'x-tenant-id': tenant,
    },
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(`Failed to fetch API catalog: ${response.status} ${response.statusText}. ${errorText}`);
  }

  const data = await response.json();
  
  try {
    return ApiCatalogSchema.parse(data);
  } catch (parseError) {
    console.error('API Catalog schema validation failed:', parseError);
    console.error('Received data:', data);
    throw new Error(`Invalid API catalog response format: ${parseError.message}`);
  }
}

export async function testApiRequest(
  token: string, 
  tenant: string, 
  request: TestRequest
): Promise<TestResponse> {
  const response = await cpFetch('/api-manager/test', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'x-tenant-id': tenant,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    throw new Error(`Test request failed: ${response.status}`);
  }

  const data = await response.json();
  return TestResponseSchema.parse(data);
}

export async function dryRunChangeSet(
  token: string,
  tenant: string,
  changeSet: ApiChangeSet
): Promise<DryRunResponse> {
  const response = await cpFetch('/changesets/dry-run', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'x-tenant-id': tenant,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(changeSet),
  });

  if (!response.ok) {
    throw new Error(`Dry run failed: ${response.status}`);
  }

  const data = await response.json();
  return DryRunResponseSchema.parse(data);
}

export async function submitChangeSet(
  token: string,
  tenant: string,
  changeSet: ApiChangeSet
): Promise<{ id: string; status: string }> {
  const response = await cpFetch('/changesets', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'x-tenant-id': tenant,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(changeSet),
  });

  if (!response.ok) {
    throw new Error(`Failed to submit ChangeSet: ${response.status}`);
  }

  return response.json();
}

// Utility functions

export function buildServiceChangeSet(
  serviceForm: any,
  routeForm: any,
  tenant: string
): ApiChangeSet {
  const serviceId = `ApiService:${serviceForm.name}`;
  const routeId = `ApiRoute:${serviceForm.name}:${routeForm.paths[0].replace(/\//g, '_')}`;

  const resources = [
    // Service
    {
      kind: 'ApiService' as const,
      id: serviceId,
      ops: [{
        op: 'add' as const,
        path: '/spec',
        value: {
          name: serviceForm.name,
          host: serviceForm.host,
          port: serviceForm.port,
          protocol: serviceForm.protocol,
          path: serviceForm.path,
          tags: [...serviceForm.tags, `tenant:${tenant}`],
        },
      }],
    },
    // Route
    {
      kind: 'ApiRoute' as const,
      id: routeId,
      ops: [{
        op: 'add' as const,
        path: '/spec',
        value: {
          service: serviceForm.name,
          paths: routeForm.paths,
          methods: routeForm.methods,
          strip_path: routeForm.strip_path,
          preserve_host: routeForm.preserve_host,
          auth: routeForm.auth,
          limits: {
            minute: routeForm.rateLimitMinute,
            sizeMB: routeForm.requestSizeMB,
          },
          cors: routeForm.corsEnabled ? {
            enabled: true,
            origins: routeForm.corsOrigins,
          } : undefined,
        },
      }],
    },
  ];

  // Add plugins
  if (routeForm.rateLimitMinute > 0) {
    resources.push({
      kind: 'ApiPlugin' as const,
      id: `ApiPlugin:${serviceForm.name}:rate-limiting`,
      ops: [{
        op: 'add' as const,
        path: '/spec',
        value: {
          service: serviceForm.name,
          name: 'rate-limiting',
          config: { minute: routeForm.rateLimitMinute },
        },
      }],
    });
  }

  if (routeForm.requestSizeMB > 0) {
    resources.push({
      kind: 'ApiPlugin' as const,
      id: `ApiPlugin:${serviceForm.name}:request-size-limiting`,
      ops: [{
        op: 'add' as const,
        path: '/spec',
        value: {
          service: serviceForm.name,
          name: 'request-size-limiting',
          config: {
            allowed_payload_size: routeForm.requestSizeMB,
            size_unit: 'megabytes',
          },
        },
      }],
    });
  }

  if (routeForm.auth === 'jwt') {
    resources.push({
      kind: 'ApiPlugin' as const,
      id: `ApiPlugin:${serviceForm.name}:jwt`,
      ops: [{
        op: 'add' as const,
        path: '/spec',
        value: {
          service: serviceForm.name,
          name: 'jwt',
          config: {},
        },
      }],
    });
  }

  if (routeForm.corsEnabled) {
    resources.push({
      kind: 'ApiPlugin' as const,
      id: `ApiPlugin:${serviceForm.name}:cors`,
      ops: [{
        op: 'add' as const,
        path: '/spec',
        value: {
          service: serviceForm.name,
          name: 'cors',
          config: {
            origins: routeForm.corsOrigins,
          },
        },
      }],
    });
  }

  return {
    resources,
    rationale: `Create ${serviceForm.name} service with ${routeForm.paths.join(', ')} routes`,
  };
}

export function generateCurlCommand(request: TestRequest): string {
  let curl = `curl -X ${request.method}`;
  
  // Add headers
  Object.entries(request.headers).forEach(([key, value]) => {
    curl += ` -H "${key}: ${value}"`;
  });

  // Add JWT if provided
  if (request.jwt) {
    curl += ` -H "Authorization: Bearer ${request.jwt}"`;
  }

  // Add body for non-GET requests
  if (request.body && request.method !== 'GET') {
    curl += ` -d '${JSON.stringify(request.body)}'`;
    curl += ` -H "Content-Type: application/json"`;
  }

  // Add timeout
  if (request.timeoutMs !== 10000) {
    curl += ` --max-time ${Math.round(request.timeoutMs / 1000)}`;
  }

  // Add URL (quoted)
  curl += ` "${request.url}"`;

  return curl;
}
